import { onBeforeMount, onMounted, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import router from '@/router'
import useNavigation from '@/common/composables/useNavigation'
import { type getDictionaryPayload, type InternalRedirectionLocation } from '@/common/types/event'
import { useVonageStore } from '@/admin/stores/vonageStore'
import { useApplicationStore } from '@/common/stores/applicationStore'
import { subscribe, unsubscribe, publish } from '@/common/services/eventBusService'
import localizationServerData from '@/common/services/serverData/localizationServerData'
import { useCtiStore } from '@/cti/stores/ctiStore'
import { useBreaksStore } from '@/agent/stores/breakStore'
import { generateDescription } from '@/agent/services/CaseDescriptionService'

export function useGlobalEvents() {
  const applicationStore = useApplicationStore()
  const navigation = useNavigation()
  const i18n = useI18n()

  const getDictionary = async ({ prefixes, cb }: getDictionaryPayload) => {
    await localizationServerData.fetch({
      prefixes,
    })

    cb(toRaw(i18n.getLocaleMessage(i18n.locale.value)))
  }

  const showTicketHandler = async (caseId: string) => {
    publish('global:case-creation-cancel')
    navigation.openCase(caseId, { force: true })
  }

  const showCustomerHandler = (customerId: string) => {
    publish('global:case-creation-cancel')
    navigation.openCustomer(customerId, { cti: !!useCtiStore().ctiCallInfo }, { force: true })
  }

  const disableNavigationHandler = () => {
    console.log('EMIT: back:disable-navigation')
    applicationStore.disabled = true
  }

  const enableNavigationHandler = () => {
    console.log('EMIT: back:enable-navigation')
    applicationStore.disabled = false
  }

  const redirectHandler = (location: InternalRedirectionLocation) => {
    if (typeof location === 'string') {
      router.push({ path: `/assist${location}`, force: true })
      return
    }

    if (typeof location === 'object' && location.url) {
      const { url, query } = location
      router.push({ path: `/assist${url}`, query, force: true })
    }
  }

  const loadWhatsappVonageNumbersHandler = () => {
    useVonageStore().passWhatsAppNumbers()
  }

  const toggleActivateWhatsappAccoundHandler = (id: number) => {
    useVonageStore().updateWhatsAppNumberActiveValue(id)
  }

  const stopBreak = () => {
    useBreaksStore().isOnBreak = false
    useCtiStore().setAgentStatus('no-break')
  }

  const generateCaseDescription = async (caseId: number) => {
    const generatedDescription = await generateDescription(caseId)
    publish('front:fill-case-description', generatedDescription)
  }

  const bindGlobalEvents = () => {
    subscribe('back:show-ticket', showTicketHandler)
    subscribe('back:show-customer', showCustomerHandler)
    subscribe('back:disable-navigation', disableNavigationHandler)
    subscribe('back:enable-navigation', enableNavigationHandler)
    subscribe('back:redirect', redirectHandler)
    subscribe('back:load-whatsapp-vonage-numbers', loadWhatsappVonageNumbersHandler)
    subscribe('back:toggle-activate-whatsapp-account', toggleActivateWhatsappAccoundHandler)
    subscribe('front-legacy:get-dictionary', getDictionary)
    subscribe('back:stop-break', stopBreak)
    subscribe('back:generate-case-description', generateCaseDescription)
  }

  const unbindGlobalEvents = () => {
    unsubscribe('back:show-ticket', showTicketHandler)
    unsubscribe('back:show-customer', showCustomerHandler)
    unsubscribe('back:disable-navigation', disableNavigationHandler)
    unsubscribe('back:enable-navigation', enableNavigationHandler)
    unsubscribe('back:redirect', redirectHandler)
    unsubscribe('back:load-whatsapp-vonage-numbers', loadWhatsappVonageNumbersHandler)
    unsubscribe('back:toggle-activate-whatsapp-account', toggleActivateWhatsappAccoundHandler)
    unsubscribe('front-legacy:get-dictionary', getDictionary)
    unsubscribe('back:generate-case-description', generateCaseDescription)
  }

  onMounted(() => {
    bindGlobalEvents()
  })

  onBeforeMount(() => {
    unbindGlobalEvents()
  })
}
